/* eslint-disable default-case */
/* eslint-disable arrow-body-style */
/* eslint-disable react/jsx-no-undef */
import { useEffect, useRef, useState } from 'react';

import { Box, Flex, AvatarBadge } from '@chakra-ui/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Button, Checkbox } from '@mesh.ai/rabbit';
import SearchBar from 'components/RabbitOKRSidePanel/Components/SearchBar';

import { Cross } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { CascadingApplyOption, CASCADING_OPTIONS, getDefaultCascadingOption } from './shared/cascadingOptions';

interface EmployeeOption {
  label: string;
  subTitle: string;
  profilePhoto: string;
  id: number;
}

const AvatarComponent = ({ name, src }: { name: string; src: string }) => {
  return <Avatar name={name} src={src} />;
};

const EmployeeSelector = ({
  options,
  handleSelection,
  selectedOptions = [],
  isCascadingAllowed = false,
}: {
  options: EmployeeOption[];
  handleSelection: (employees: EmployeeOption[], applyToAllLevels: boolean) => void;
  selectedOptions: EmployeeOption[];
  isCascadingAllowed: boolean;
}) => {
  const [searchText, setSearchText] = useState('');
  const optionsListRef = useRef(null);
  const [localSelectedOptions, setLocalSelectedOptions] = useState<EmployeeOption[]>(selectedOptions);

  const handleInputChange = (e) => {
    setSearchText(e?.target?.value);
  };

  const clearSearchText = () => {
    setSearchText('');
  };

  useEffect(() => {
    clearSearchText();
    setLocalSelectedOptions(selectedOptions);
  }, [selectedOptions]);

  const filteredSearchOptions = options?.filter((option) =>
    option?.label?.toLowerCase().includes(searchText?.toLowerCase())
  );

  const displayOptions = searchText ? filteredSearchOptions : options;

  const [appliesTo, setAppliesTo] = useState(getDefaultCascadingOption());

  const handleEmployeeToggle = (employee: EmployeeOption) => {
    const isSelected = localSelectedOptions.some((option) => option.id === employee.id);

    if (isSelected) {
      setLocalSelectedOptions(localSelectedOptions.filter((option) => option.id !== employee.id));
    } else {
      setLocalSelectedOptions([...localSelectedOptions, employee]);
    }
  };

  const handleClearAll = () => {
    clearSearchText();
    setLocalSelectedOptions([]);
  };

  const handleSave = () => {
    handleSelection(localSelectedOptions, appliesTo.value === CascadingApplyOption.ALL_LEVELS);
  };

  const handleRemoveEmployee = (employeeId: number) => {
    setLocalSelectedOptions(localSelectedOptions.filter((option) => option.id !== employeeId));
  };

  return (
    <Flex flexDirection="column">
      <Box padding="8px" display="flex" flexDirection="column" gap="4px">
        <SearchBar
          value={searchText}
          placeholder="Search employees"
          onChange={(e) => handleInputChange(e)}
          searchRef={optionsListRef}
          onClearInput={clearSearchText}
        />

        {/* Selected Employees Display */}
        {localSelectedOptions.length > 0 && (
          <Box py={2} pl={1}>
            <Flex alignItems="center" columnGap={2}>
              <TextBodyMedium color="#666666" bold>
                {`Selected (${localSelectedOptions.length}) : `}
              </TextBodyMedium>
              <AvatarGroup size={24}>
                {localSelectedOptions.map((emp) => {
                  const { id, label, profilePhoto } = emp;
                  return (
                    <Tooltip key={id} label={label}>
                      <Avatar isClearable name={label} src={profilePhoto}>
                        <AvatarBadge
                          style={{ top: '-10px', bottom: 'unset' }}
                          boxSize="0.9rem"
                          cursor="pointer"
                          onClick={() => handleRemoveEmployee(id)}
                          bg="white"
                        >
                          <Cross boxSize="12px" color="#666666" />
                        </AvatarBadge>
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
            </Flex>
          </Box>
        )}

        <Box maxH="280px" overflowX="hidden">
          {displayOptions &&
            displayOptions.map((option) => {
              const { label, subTitle: email, profilePhoto, id } = option;
              const isSelected = localSelectedOptions.some((selected) => selected.id === id);
              return (
                <Flex
                  key={`${id ?? email}-${isSelected}`}
                  flexDirection="row"
                  padding="8px 0px"
                  justifyContent="space-between"
                  alignItems="center"
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                >
                  <Box pl={1} flex={1}>
                    <ListItemComp
                      title={label}
                      LeftComponent={AvatarComponent}
                      leftComponentProps={{ name: label, src: profilePhoto }}
                      subTitle={email}
                      isBoldText={false}
                      withoutBackground
                    />
                  </Box>
                  <Box pr={2}>
                    <Checkbox checked={isSelected} onCheckedChange={() => handleEmployeeToggle(option)} />
                  </Box>
                </Flex>
              );
            })}
        </Box>
      </Box>

      <Box display="flex" flexDirection="column">
        {isCascadingAllowed && (
          <Box
            display="flex"
            padding="8px 12px"
            alignItems="center"
            gap="8px"
            alignSelf="stretch"
            background="var(--Neutral-FDFDFD, #FDFDFD)"
            boxShadow="0px -1px 2px 0px rgba(0, 0, 0, 0.08)"
          >
            <Flex align="center" gap="1">
              <TextBodyMedium color="#666666" mr={1}>
                Applies to :
              </TextBodyMedium>
              <Select
                value={appliesTo.value}
                onValueChange={(value) => {
                  const selectedApplyOption = CASCADING_OPTIONS.find((option) => option.value === value);
                  if (selectedApplyOption) setAppliesTo(selectedApplyOption);
                }}
              >
                <SelectTrigger style={{ width: '180px' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ width: '200px', zIndex: 1511 }}>
                  {CASCADING_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Flex>
          </Box>
        )}
        <Flex h="72px" bg="surface.F5F7F9" justifyContent="space-between" alignItems="center" px="16px">
          <Button variant="text" status="error" style={{ background: 'transparent' }} onClick={handleClearAll}>
            Clear all
          </Button>
          <Button variant="filled" style={{ width: '80px' }} onClick={handleSave} disabled={false}>
            Save
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export { EmployeeSelector };
